import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../app/slices/TrainingSlice";
import SequenceAttachmentMarker, { AttachmentMember } from "./SequenceAttachmentMarker";
import useAttachmentMarkerParts from "./useAttachmentMarkerParts";
import useTakeAttachmentsOnMap from "./useTakeAttachmentsOnMap";

/**
 * The take's own attachments on the map, docked like the sequence state draws
 * them (no dot: they are the take's, not the sequence's). Display only: the
 * actor and prop markers below keep their popovers.
 */
export default function Viewport3DTakeAttachmentLayer() {
  const take = useSelector(selectSelectedTake3D);
  const attachments = useTakeAttachmentsOnMap();
  const { points, takeColor, actorName, propName, actorMember, propMember, placementOf } =
    useAttachmentMarkerParts(take);

  if (!take || !attachments) return null;
  const positionOf = (actorId: string) =>
    take.actors?.find((actor) => actor.actorId === actorId)?.positionId;

  // One marker per host: an actor can hold more than one prop.
  const markers = new Map<
    string,
    { positionId?: string; host: AttachmentMember; hostName: string; guests: AttachmentMember[]; names: string[]; held: boolean }
  >();
  const add = (
    key: string,
    positionId: string | undefined,
    host: AttachmentMember,
    hostName: string,
    guest: AttachmentMember,
    guestName: string,
    held: boolean,
  ) => {
    const existing = markers.get(key);
    if (existing) {
      existing.guests.push(guest);
      existing.names.push(guestName);
    } else markers.set(key, { positionId, host, hostName, guests: [guest], names: [guestName], held });
  };

  attachments.held.forEach(({ actorId, propId }) =>
    add(`held:${actorId}`, positionOf(actorId), actorMember(actorId), actorName(actorId), propMember(propId), propName(propId), true),
  );
  attachments.riding.forEach(({ actorId, propId }) =>
    add(`riding:${propId}`, placementOf(propId), propMember(propId), propName(propId), actorMember(actorId), actorName(actorId), false),
  );

  return (
    <>
      {[...markers.entries()].map(([key, marker]) => {
        const point = marker.positionId ? points.get(marker.positionId) : undefined;
        if (!point) return null;
        return (
          <SequenceAttachmentMarker
            key={key}
            x={point.x}
            y={point.y}
            host={marker.host}
            guests={marker.guests}
            held={marker.held}
            takeColor={takeColor}
            title={
              marker.held
                ? `${marker.hostName} holds ${marker.names.join(", ")}`
                : `${marker.names.join(", ")} on ${marker.hostName}`
            }
          />
        );
      })}
    </>
  );
}
